<template>
	<div class="animated fadeIn">

		<b-row class="mb-3 align-items-center">
			<b-col>
				<h4 class="mb-0"><i class="fa fa-credit-card mr-2 text-primary"></i>Tipsoi Pay Dashboard</h4>
				<small class="text-muted">Billing state for every client, as Tipsoi Pay last reported it</small>
			</b-col>
			<b-col class="text-right">
				<b-button variant="outline-secondary" size="sm" @click="load" :disabled="loading">
					<i class="icon-reload mr-1"></i>Refresh
				</b-button>
			</b-col>
		</b-row>

		<div v-if="loading" class="text-center py-5">
			<b-spinner variant="info" style="width:3rem;height:3rem;"></b-spinner>
		</div>

		<div v-else>
			<b-alert v-if="loadError" show variant="warning" class="small">{{ loadError }}</b-alert>

			<template v-else>
				<!-- Connection ------------------------------------------------ -->
				<b-card no-body class="mb-3">
					<div slot="header"><i class="icon-link mr-2"></i>Tipsoi Pay connection</div>
					<b-card-body class="py-3">
						<b-row>
							<b-col md="4" class="mb-2">
								<div class="meta-label">Status</div>
								<span class="state" :class="summary.billingConfigured ? 'state-ok' : 'state-off'">
									<span class="dot"></span>{{ summary.billingConfigured ? 'Configured' : 'Not configured' }}
								</span>
							</b-col>
							<b-col md="4" class="mb-2">
								<div class="meta-label">Instance</div>
								<div><code>{{ summary.billingBaseUrl || '—' }}</code></div>
							</b-col>
							<b-col md="4" class="mb-2">
								<div class="meta-label">Clients linked</div>
								<div class="num">{{ summary.linked }} <span class="text-muted">of {{ summary.totalOffices }}</span></div>
							</b-col>
						</b-row>
						<b-alert v-if="!summary.billingConfigured" show variant="warning" class="mb-0 mt-2 small">
							<code>billing.api.url</code> / <code>billing.api.key</code> are not set on this
							environment, so new clients cannot be onboarded to Tipsoi Pay from this panel.
							The figures below still work — they come from billing state already stored here.
						</b-alert>
					</b-card-body>
				</b-card>

				<!-- Counters --------------------------------------------------- -->
				<b-row class="mb-3">
					<b-col sm="6" md="3" lg="2" class="mb-2">
						<div class="stat-card stat-bad">
							<div class="stat-num">{{ summary.overdue }}</div>
							<div class="stat-label">Overdue</div>
						</div>
					</b-col>
					<b-col sm="6" md="3" lg="2" class="mb-2">
						<div class="stat-card stat-warn">
							<div class="stat-num">{{ summary.dueSoon }}</div>
							<div class="stat-label">Due ≤ 7 days</div>
						</div>
					</b-col>
					<b-col sm="6" md="3" lg="2" class="mb-2">
						<div class="stat-card stat-ok">
							<div class="stat-num">{{ summary.current }}</div>
							<div class="stat-label">Current</div>
						</div>
					</b-col>
					<b-col sm="6" md="3" lg="2" class="mb-2">
						<div class="stat-card">
							<div class="stat-num">{{ summary.noDueDate }}</div>
							<div class="stat-label">No due date</div>
						</div>
					</b-col>
					<b-col sm="6" md="3" lg="2" class="mb-2">
						<div class="stat-card">
							<div class="stat-num">{{ summary.notLinked }}</div>
							<div class="stat-label">Not linked</div>
						</div>
					</b-col>
					<b-col sm="6" md="3" lg="2" class="mb-2">
						<div class="stat-card stat-info">
							<div class="stat-num">{{ summary.enforcing }}</div>
							<div class="stat-label">Enforcing</div>
						</div>
					</b-col>
				</b-row>

				<b-row>
					<!-- Ageing ------------------------------------------------- -->
					<b-col lg="5" class="mb-3">
						<b-card no-body class="h-100">
							<div slot="header"><i class="fa fa-hourglass-half mr-2"></i>Ageing of overdue clients</div>
							<b-card-body class="py-3">
								<div v-if="!summary.overdue" class="text-muted small py-2">
									No client is past its due date.
								</div>
								<div v-else>
									<div v-for="bucket in summary.ageing" :key="bucket.label" class="age-row">
										<div class="age-label">{{ bucket.label }}</div>
										<div class="age-bar">
											<div class="age-fill" :style="{width: barWidth(bucket.offices) + '%'}"></div>
										</div>
										<div class="age-num num">{{ bucket.offices }}</div>
									</div>
								</div>
							</b-card-body>
						</b-card>
					</b-col>

					<!-- Most overdue ------------------------------------------- -->
					<b-col lg="7" class="mb-3">
						<b-card no-body class="h-100">
							<div slot="header" class="d-flex justify-content-between align-items-center">
								<span><i class="fa fa-exclamation-triangle mr-2"></i>Longest overdue</span>
								<router-link to="/tipsoi-pay/clients?filter=OVERDUE" class="small">See all</router-link>
							</div>
							<div class="table-responsive">
								<table class="table table-sm table-striped mb-0">
									<thead>
										<tr>
											<th>Client</th>
											<th class="text-right">Overdue</th>
											<th>Due date</th>
											<th class="text-right">Last paid</th>
											<th></th>
										</tr>
									</thead>
									<tbody>
										<tr v-for="c in summary.mostOverdue" :key="c.officeId">
											<td>
												{{ c.officeName }}
												<b-badge v-if="c.enforcing" variant="info" class="ml-1">enforcing</b-badge>
											</td>
											<td class="text-right num text-danger">{{ -c.daysToDue }} d</td>
											<td class="small text-muted">{{ formatDate(c.billingDueDate) }}</td>
											<td class="text-right num">{{ formatMoney(c.billingCashPaidAmount) }}</td>
											<td class="text-right">
												<a v-if="c.billingInvoiceUrl" :href="c.billingInvoiceUrl" target="_blank"
													 rel="noopener" class="small">Open ↗</a>
											</td>
										</tr>
										<tr v-if="!summary.mostOverdue.length">
											<td colspan="5" class="text-center text-muted py-4">Nothing overdue.</td>
										</tr>
									</tbody>
								</table>
							</div>
						</b-card>
					</b-col>
				</b-row>

				<!-- Last settlements ------------------------------------------- -->
				<b-card no-body class="mb-3">
					<b-card-body class="py-3">
						<b-row class="align-items-center">
							<b-col md="5">
								<div class="meta-label">Sum of last settlements</div>
								<div class="big-num">{{ formatMoney(summary.lastPaymentAmountTotal) }}</div>
							</b-col>
							<b-col md="7">
								<p class="small text-muted mb-0">
									This is each linked client's <strong>most recent</strong> payment added together —
									whenever those payments happened. It is <strong>not</strong> a figure for any period,
									and it is not comparable month to month. Period collections need the Tipsoi Pay
									reporting endpoints described below.
								</p>
							</b-col>
						</b-row>
					</b-card-body>
				</b-card>

				<!-- What this page cannot show --------------------------------- -->
				<b-card no-body border-variant="warning">
					<div slot="header" class="text-warning">
						<i class="fa fa-info-circle mr-2"></i>Not available on this dashboard yet
					</div>
					<b-card-body class="py-3">
						<p class="small text-muted mb-2">{{ reporting.reason }}</p>
						<ul class="small text-muted mb-0">
							<li v-for="item in reporting.unavailable" :key="item">{{ item }}</li>
						</ul>
					</b-card-body>
				</b-card>
			</template>
		</div>
	</div>
</template>

<script>
import moment from 'moment';
import payService from '@/services/payService';

export default {
	name: 'PayDashboard',

	data() {
		return {
			loading: false,
			loadError: '',
			summary: emptySummary()
		};
	},

	computed: {
		reporting() {
			return this.summary.reporting || {reason: '', unavailable: []};
		},
		maxAgeBucket() {
			const counts = (this.summary.ageing || []).map(b => b.offices);
			return counts.length ? Math.max.apply(null, counts) : 0;
		}
	},

	methods: {
		load() {
			this.loading = true;
			this.loadError = '';

			payService.getSummary((data) => {
				this.loading = false;
				if (!data || data.available === false) {
					this.loadError = (data && data.error) || 'The Tipsoi Pay summary is not available.';
					return;
				}
				this.summary = Object.assign(emptySummary(), data);
			}, (err) => {
				this.loading = false;
				this.loadError = err;
			});
		},

		barWidth(count) {
			// Bars are relative to the largest bucket, so a single overdue client still reads as a
			// full bar rather than an invisible sliver.
			if (!this.maxAgeBucket) return 0;
			return Math.max(2, Math.round((count / this.maxAgeBucket) * 100));
		},

		formatDate(millis) {
			if (!millis) return '—';
			return moment(millis).format('DD MMM YYYY');
		},

		formatMoney(amount) {
			if (amount === null || amount === undefined) return '—';
			return '৳ ' + Number(amount).toLocaleString('en-IN');
		}
	},

	created() {
		this.load();
	}
};

function emptySummary() {
	return {
		totalOffices: 0, linked: 0, notLinked: 0,
		overdue: 0, dueSoon: 0, current: 0, noDueDate: 0, enforcing: 0,
		lastPaymentAmountTotal: 0,
		ageing: [], mostOverdue: [],
		billingConfigured: false, billingBaseUrl: null,
		reporting: {reason: '', unavailable: []}
	};
}
</script>

<style scoped>
.stat-card {
	background: #fff;
	border: 1px solid #e4e7ea;
	border-left: 3px solid #c8ced3;
	border-radius: 4px;
	padding: 0.75rem 1rem;
	height: 100%;
}

.stat-card.stat-bad { border-left-color: #f86c6b; }
.stat-card.stat-warn { border-left-color: #ffc107; }
.stat-card.stat-ok { border-left-color: #4dbd74; }
.stat-card.stat-info { border-left-color: #63c2de; }

.stat-num {
	font-size: 1.5rem;
	font-weight: 600;
	line-height: 1.2;
	font-variant-numeric: tabular-nums;
}

.stat-label,
.meta-label {
	font-size: 0.72rem;
	text-transform: uppercase;
	letter-spacing: 0.05em;
	color: #73818f;
}

.big-num {
	font-size: 1.75rem;
	font-weight: 600;
	font-variant-numeric: tabular-nums;
	line-height: 1.2;
}

.state {
	display: inline-flex;
	align-items: center;
	font-size: 0.9rem;
}

.state .dot {
	width: 8px;
	height: 8px;
	border-radius: 50%;
	margin-right: 0.4rem;
	background: #c8ced3;
}

.state-ok { color: #2f8f52; }
.state-ok .dot { background: #4dbd74; }
.state-off { color: #73818f; }

.age-row {
	display: flex;
	align-items: center;
	gap: 0.6rem;
	margin-bottom: 0.55rem;
}

.age-label {
	width: 6.5rem;
	font-size: 0.8rem;
	color: #73818f;
	flex: none;
}

.age-bar {
	flex: 1;
	height: 10px;
	background: #f0f3f5;
	border-radius: 5px;
	overflow: hidden;
}

.age-fill {
	height: 100%;
	background: #f86c6b;
}

.age-num {
	width: 2.5rem;
	text-align: right;
	font-size: 0.85rem;
	flex: none;
}

.num { font-variant-numeric: tabular-nums; }
</style>
